<template>
	<section class="section-base form-section-base">
		<div class="form-section-base__block-outer">
			<div class="form-section-base__block-inner">
				<div class="form-section-base__title">
					Вид документа, на основании которого совершается исполнительная надпись:
				</div>
				<div class="form-base">
					<v-row>
						<v-col>
							<v-radio-group
								v-model="form.notarialWritCode"
								column
								:disabled="isSurcharge"
							>
								<v-radio
									v-for="item in notarialWritCodeArr"
									:key="item.value"
									class="pb-3"
									:value="item.value"
									:label="item.label"
									:disabled="item.disabled"
								/>
							</v-radio-group>
						</v-col>
						<v-col cols="12">
							<AttachDocumentMultiple
								v-model="$v.documents.$model"
								:errorMessages="getValidationErrorText($v.documents)"
								:disabled="isSurcharge"
								:withSelect="statementType === StatementType.NOTICE_RECOVERY && isNoticeRecoveryClassifierEnable"
								:documentTypes="documentTypes"
								translationKey="noticeRecovery.document.attachMultiple"
							/>
						</v-col>
					</v-row>
				</div>
			</div>
		</div>
		<div class="form-section-base__block-outer">
			<div class="form-section-base__block-inner">
				<v-row>
					<v-col cols="12">
						<div class="form-base__title">Сведения о договоре</div>
					</v-col>
					<v-col class="12">
						<v-radio-group
							v-model="$v.form.agreementType.$model"
							row
							:disabled="isSurcharge"
						>
							<v-radio label="совершен в электронной форме" value="1"></v-radio>
							<v-radio
								label="совершен на бумажном носителе"
								value="2"
							></v-radio>
						</v-radio-group>
					</v-col>
				</v-row>
				<v-row>
					<v-col cols="3">
						<DateFieldCustom
							label="Дата договора"
							:value="$v.form.agreementDate.$model"
							:errorMessage="$v.form.agreementDate"
							:disabled="isSurcharge"
							:max="new Date().toISOString()"
							@change="$v.form.agreementDate.$model = $event"
							@blur="$v.form.agreementDate.$touch"
						/>
					</v-col>
				</v-row>
				<v-row>
					<v-col cols="3">
						<v-radio-group
							v-model="$v.form.contractNumberType.$model"
							column
							:disabled="isSurcharge"
						>
							<v-radio class="pb-3" label="договор без номера" value="1"/>
							<v-radio class="pb-3" label="номер договора" value="2"/>
						</v-radio-group>
					</v-col>
					<v-col cols="3">
						<v-text-field
							v-model="$v.form.agreementNumber.$model"
							:errorMessages="getValidationErrorText($v.form.agreementNumber)"
							:disabled="isSurcharge"
							class="mt-6"
							v-if="form.contractNumberType === '2'"
							label=""
							outlined
							@blur="$v.form.agreementNumber.$touch"
						/>
					</v-col>
				</v-row>
				<v-row v-if="isSuperDoc">
					<v-col cols="6">
						<v-text-field
							v-model="$v.form.agreementRegNum.$model"
							:errorMessages="getValidationErrorText($v.form.agreementRegNum)"
							:disabled="isSurcharge"
							outlined
							label="регистрационный номер в реестре нотариальных действий"
							@blur="$v.form.agreementRegNum.$touch"
						/>
					</v-col>
					<v-col v-if="notaryInfoRequired" cols="10">
						<v-textarea
							v-model="$v.form.agreementNotary.$model"
							:errorMessages="getValidationErrorText($v.form.agreementNotary)"
							:disabled="isSurcharge"
							outlined
							label="Сведения о нотариусе, удостоверившем сделку (заполняется, если сделка удостоверена до 07.01.2018)"
							@blur="$v.form.agreementNotary.$touch"
						/>
					</v-col>
				</v-row>
			</div>
		</div>
		<v-container>
			<Footer
				@next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
				@prev-btn-click="btnSaveClickHandler(StepDirection.PREV)"
				:loading-next="loadingNext"
			/>
		</v-container>
	</section>
</template>

<script>
import cloneDeep from 'clone-deep';

import {DocTypeMock} from '@/utils/mocks.ts';
import {
  DATE_FORMAT,
  DATE_FUTURE,
  DATE_PAST, DOCS_REQUIREMENTS,
  EMOJI,
  getValidationErrorText, REQUIRED_DOC, TRAILING_AND_LEADING_SPACES,
} from '@/utils/validate';
import { isNoticeRecoveryClassifierEnable } from '@/app.config';
import {required, maxLength, and, requiredIf} from 'vuelidate/lib/validators';
import {ServicesModule} from '@/store/modules/services';
import {dateFormat, checkInvalid, leaveStep} from '@/utils/services';
import AttachDocumentMultiple from '@/components/base/AttachDocument/AttachDocumentMultiple';
import DateFieldCustom from '@/components/base/DateFieldCustom';
import Footer from '@/components/services/elements/Footer';
import {StepDirection, StepName} from '@/types/services';
import {configNotarialWritCodeNR} from '@/app.config';
import {NoticeRecoveryStep, StatementType} from '@/constants';
import {UploadDocumentTypes} from "@/utils/uploadDocumentTypes";

export default {
	name: 'RecoveryDetails',
	props: {
		loadingNext: {
			type: Boolean,
			required: true,
		},
		isAlternativeServiceEdit: {
			type: Boolean,
			required: true,
		},
		isSurcharge: {
			type: Boolean,
			required: true,
		},
		statementType: {
			type: String,
			required: true,
		},
	},
	components: {
		DateFieldCustom,
		AttachDocumentMultiple,
		Footer,
	},
	validations() {
		const agreementNotary = {
			maxLength: maxLength(4000),
			EMOJI,
			TRAILING_AND_LEADING_SPACES
		};

		const documents = {
			DOCUMENTS_MIN_LENGTH: requiredIf(
				() =>
					!(
						this.isAlternativeServiceEdit &&
						(this.isApplicantOrganization ||
							this.isApplicantForeignOrganization)
					)
			),
		};
    if (!this.isAlternativeServiceEdit) {
      documents.DOCS_REQUIREMENTS = DOCS_REQUIREMENTS(this.documents)
      documents.REQUIRED_DOC = REQUIRED_DOC(this.documents, '11101')
    }

		const form = {
			notarialWritCode: {},
			agreementType: {},
			contractNumberType: {},
			agreementDate: {
				required,
				DATE: and(DATE_FORMAT, DATE_FUTURE, DATE_PAST),
			},
			agreementNumber: {
				maxLength: maxLength(50),
				EMOJI,
				TRAILING_AND_LEADING_SPACES
			},
		};

		if (this.isSuperDoc) {
			form.agreementRegNum = {
				required,
				maxLength: maxLength(40),
				EMOJI,
				TRAILING_AND_LEADING_SPACES
			};
		}

		if (this.notaryInfoRequired) {
			agreementNotary.required = required;
		}

		form.agreementNotary = agreementNotary;

		return {
			form: form,
			documents,
		};
	},
	data: () => ({
		StepDirection,
		StepName,
		leaveStep,
		DocTypeMock,
		getValidationErrorText,
		invalidFields: [],
		StatementType,
    isNoticeRecoveryClassifierEnable,

		//form data
		form: {
			notarialWritCode: '002',
			agreementType: '1',
			agreementDate: '',
			contractNumberType: '1',
			agreementNumber: '',
			agreementRegNum: '',
			agreementNotary: '',
		},
		documents: [],
		notarialWritCodeArrRaw: [
			{
				value: '001',
				label:
					'нотариально удостоверенная сделка, устанавливающая денежные обязательства или обязательства по передаче имущества',
				disabled: false,
			},
			{
				value: '002',
				documentsCodesList: ['10201', '10202', '10203', '10204', '10299', '11101', '11201', '11202', '11203', '11299',],
				label:
					'кредитный договор, кредитором по которому выступает не являющаяся микрофинансовой организация (в договоре или дополнительных соглашениях к нему указаны условия о возможности взыскания задолженности по исполнительной надписи нотариуса)',
				disabled: false,
			},
			{
				value: '003',
				label:
					'договор займа, кредитором по которому выступает единый институт развития в жилищной сфере (в договоре или дополнительных соглашениях к нему указаны условия о возможности взыскания задолженности по исполнительной надписи нотариуса)',
				disabled: false,
			},
			{
				value: '004',
				label:
					'кредитный договор, кредитором по которому на основании уступки прав (требований) выступает единый институт развития в жилищной сфере (в договоре или дополнительных соглашениях к нему указаны условия о возможности взыскания задолженности по исполнительной надписи нотариуса)',
				disabled: false,
			},
			{
				value: '005',
				documentsCodesList: ['10401', '10402', '10499', '11101', '11201', '11202', '11203', '11299',],
				label:
					'договор поручительства, предусматривающий солидарную ответственность поручителя по кредитному договору (в договоре поручительства указаны условия о возможности взыскания задолженности по исполнительной надписи нотариуса)',
				disabled: false,
			},
			{
				value: '006',
				documentsCodesList: ['10501', '10502', '10599', '11101', '11201', '11202', '11203', '11299',],
				label:
					'договор об оказании услуг связи в случае неисполнения обязательств по оплате услуг в сроки, установленные в договоре об оказании услуг связи (в договоре указаны условия о возможности взыскания задолженности по исполнительной надписи нотариуса)',
				disabled: false,
			},
			{
				value: '007',
				label: 'договор займа (залоговый билет)',
				disabled: false,
			},
			{
				value: '008',
				label: 'договор хранения',
				disabled: false,
			},
			{
				value: '009',
				label: 'договор проката',
				disabled: false,
			},
		],
	}),
	created() {
		// выставляем по умолчанию: первый из открытых в конфиге
		this.form.notarialWritCode =
			!configNotarialWritCodeNR || configNotarialWritCodeNR === 'all'
				? '002'
				: configNotarialWritCodeNR.split(';')[0];
		this.prefillDraft();
	},
	methods: {
		touchFields() {
			this.invalidFields = [];
			this.$v.$touch();
			checkInvalid(this.$v, this.invalidFields);
		},

		assemblyModel() {
			let ENRDStore = {};

			if (
				ServicesModule.statement &&
				ServicesModule.statement.executiveNoticesRecoveryDetails
			) {
				ENRDStore = {
					...ServicesModule.statement.executiveNoticesRecoveryDetails,
				};
			}
			return {
				executiveNoticesRecoveryDetails: {
					...ENRDStore,
					...this.form,
				},
				notarialDocuments: cloneDeep(this.documents),
			};
		},

		prefillDraft() {
			if (!ServicesModule.statement.executiveNoticesRecoveryDetails) return;

			const modelStore = cloneDeep(
				ServicesModule.statement.executiveNoticesRecoveryDetails
			);
			const filesStore = cloneDeep(ServicesModule.statement.notarialDocuments);

			this.form = {
				...this.form,
				...modelStore,
				contractNumberType: modelStore.agreementNumber ? '2' : '1',
			};
			this.documents = filesStore ? cloneDeep(filesStore) : [];
		},

		btnSaveClickHandler(direction) {
			leaveStep({
				stepThis: this,
				direction,
				step: NoticeRecoveryStep.RECOVERY_DETAILS,
			});
		},
	},
	computed: {
		isSuperDoc: function () {
			return this.form.notarialWritCode === '001';
		},

		notaryInfoRequired: function () {
			return (
				this.isSuperDoc &&
				+new Date(dateFormat(this.form.agreementDate)) <=
				+new Date('2018-01-07')
			);
		},

		notarialWritCodeArr() {
			if (
				!configNotarialWritCodeNR ||
				configNotarialWritCodeNR.toLowerCase() === 'all'
			) {
				return this.notarialWritCodeArrRaw;
			}
			return this.notarialWritCodeArrRaw.map((item) => ({
				...item,
				disabled: !configNotarialWritCodeNR.split(';').includes(item.value),
			})).filter(item => !item.disabled);
		},

		isApplicantOrganization() {
			return Boolean(ServicesModule.statement.subjects.applicant.organization);
		},

		isApplicantForeignOrganization() {
			return Boolean(
				ServicesModule.statement.subjects.applicant.foreignOrganization
			);
		},

		documentTypes() {
			const code = this.form.notarialWritCode
			let groups = UploadDocumentTypes.filter((item) => ['10200', '10400', '10500', '11100', '11200'].includes(item.groupCode))
			let codes
			if (code) {
				codes = this.notarialWritCodeArr.filter(item => item.value === code)[0].documentsCodesList
				if (!codes) return groups
				groups = groups.map((item) => {
					return {
						...item,
						documents: item.documents.filter((doc => codes.includes(doc.value)))
					}
				})
			}
			return groups
		}
	},
};
</script>

<style scoped></style>
